import React from "react";

export default function Sidebar({ 
  activeModule, 
  onSelectModule, 
  onBackToDashboard,
  isSidebarOpen,
  onToggleSidebar,
  onCloseSidebar,
  onLogout
}) {
  const modules = [
    { id: "report", label: "Crop Report", icon: "assessment" },
    { id: "crop", label: "Crop Identification", icon: "spa" },
    { id: "pest", label: "Pest & Disease Identification", icon: "bug_report" },
    { id: "stage", label: "Crop Stage Identification", icon: "timeline" },
    { id: "annotation", label: "Annotation Studio", icon: "draw" }
  ];


  return (
    <>
      {/* 1. Desktop Sidebar */}
      <aside 
        className={`hidden lg:flex fixed left-0 top-0 min-h-dvh bg-secondary dark:bg-on-secondary-fixed-variant shadow-lg flex-col py-stack-lg z-50 transition-all duration-300 ${
          isSidebarOpen ? "w-[var(--sidebar-expanded,240px)]" : "w-[var(--sidebar-collapsed,72px)]"
        }`}
      >
        {/* Brand Header & Hamburger Toggle & Bell */}
        <div className={`px-4 mb-8 flex items-center ${isSidebarOpen ? "justify-between" : "justify-center"}`}>
          {isSidebarOpen && (
            <div className="flex items-center gap-3 select-none">
              <div className="w-9 h-9 rounded-lg bg-primary-fixed-dim/20 flex items-center justify-center shrink-0">
                <span className="material-symbols-outlined text-primary-fixed-dim" style={{ fontVariationSettings: "'FILL' 1" }}>eco</span>
              </div>
              <div className="flex flex-col">
                <span className="font-headline-md text-headline-md font-bold text-primary-fixed-dim tracking-tight">
                  CROPSCAN
                </span>
              </div>
            </div>
          )}

          <button 
            onClick={onToggleSidebar}
            className="p-2 rounded-xl text-surface-variant/70 hover:text-primary-fixed-dim hover:bg-primary/10 transition-colors flex items-center justify-center shrink-0"
            title={isSidebarOpen ? "Collapse Sidebar" : "Expand Sidebar"}
          >
            <span className="material-symbols-outlined text-[22px]">
              {isSidebarOpen ? "menu_open" : "menu"}
            </span>
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="flex-1 flex flex-col gap-1.5 px-2">
          {modules.map((mod) => {
            const isActive = activeModule === mod.id;
            return (
              <button
                key={mod.id}
                onClick={() => {
                  onSelectModule(mod.id);
                  if (onCloseSidebar) onCloseSidebar();
                }}
                title={mod.label}
                className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200 text-left ${
                  !isSidebarOpen ? "justify-center px-0" : ""
                } ${
                  isActive 
                    ? "text-primary-fixed-dim font-bold bg-on-secondary-fixed-variant/30" 
                    : "text-surface-variant/70 hover:text-surface-variant hover:bg-primary/10"
                }`}
              >
                <span 
                  className="material-symbols-outlined text-[22px] shrink-0" 
                  style={isActive ? { fontVariationSettings: "'FILL' 1" } : {}}
                >
                  {mod.icon}
                </span>
                {isSidebarOpen && (
                  <span className="font-body-lg text-body-lg truncate">
                    {mod.id === "pest" ? "Pest & Disease Identification" : mod.id === "annotation" ? "Annotation Studio" : mod.label}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* 2. Mobile Drawer Sidebar */}
      {isSidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-[100] flex">
          <div 
            onClick={onCloseSidebar}
            className="fixed inset-0 bg-black/60 backdrop-blur-xs transition-opacity duration-300"
          ></div>

          <aside className="relative w-[280px] max-w-[80vw] bg-secondary dark:bg-on-secondary-fixed-variant h-full shadow-2xl flex flex-col py-6 px-4 z-10 animate-in slide-in-from-left duration-300">
            <div className="flex items-center justify-between mb-8 px-2">
              <div className="flex items-center gap-3 select-none">
                <div className="w-9 h-9 rounded-lg bg-primary-fixed-dim/20 flex items-center justify-center">
                  <span className="material-symbols-outlined text-primary-fixed-dim" style={{ fontVariationSettings: "'FILL' 1" }}>eco</span>
                </div>
                <div className="flex flex-col">
                  <span className="font-headline-md text-headline-md font-bold text-primary-fixed-dim tracking-tight">CROPSCAN</span>
                </div>
              </div>

              <button 
                onClick={onCloseSidebar}
                className="p-2 rounded-full text-surface-variant/70 hover:text-primary-fixed-dim hover:bg-primary/10"
              >
                <span className="material-symbols-outlined text-[22px]">close</span>
              </button>
            </div>

            <nav className="flex-1 flex flex-col gap-2">
              {modules.map((mod) => {
                const isActive = activeModule === mod.id;
                return (
                  <button
                    key={mod.id}
                    onClick={() => {
                      onSelectModule(mod.id);
                      onCloseSidebar();
                    }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all font-body-lg ${
                      isActive 
                        ? "text-primary-fixed-dim font-bold bg-on-secondary-fixed-variant/30" 
                        : "text-surface-variant/75 hover:bg-primary/10"
                    }`}
                  >
                    <span 
                      className="material-symbols-outlined text-[22px]" 
                      style={isActive ? { fontVariationSettings: "'FILL' 1" } : {}}
                    >
                      {mod.icon}
                    </span>
                    <span>
                      {mod.id === "pest" ? "Pest & Disease Identification" : mod.id === "annotation" ? "Annotation Studio" : mod.label}
                    </span>
                  </button>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* 3. Mobile Bottom Nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 w-full h-16 bg-secondary dark:bg-on-secondary-fixed-variant shadow-[0_-4px_16px_rgba(0,0,0,0.1)] border-t border-surface-variant/20 flex flex-row justify-around items-center z-40 px-2">
        {modules.map((mod) => {
          const isActive = activeModule === mod.id;
          return (
            <button
              key={mod.id}
              onClick={() => onSelectModule(mod.id)}
              className={`flex-1 flex flex-col items-center justify-center py-1 transition-all border-t-2 ${
                isActive 
                  ? "text-primary-fixed-dim font-bold border-primary-fixed-dim bg-on-secondary-fixed-variant/10" 
                  : "text-surface-variant/75 border-transparent"
              }`}
            >
              <span 
                className="material-symbols-outlined text-[20px]" 
                style={isActive ? { fontVariationSettings: "'FILL' 1" } : {}}
              >
                {mod.icon}
              </span>
              <span className="text-[10px] mt-0.5 tracking-tight font-medium truncate w-full text-center">
                {mod.label}
              </span>
            </button>
          );
        })}
      </nav>

    </>
  );
}
